import {
  seqirusConfig,
  gskConfig,
  hexalConfig,
  incyteConfig,
  novartisConfig,
} from '@common/config';
import { Client, HexalInstance, NovartisInstance } from '@common/enums';
import { IInstanceConfig } from '@common/interfaces';
import {
  IMessage,
  INlpResponse,
  IQuestionOption,
  IReference,
  ITopic,
  ITopicGroup,
  ITopicTag,
  MessageType,
  ReferenceType,
  TNullable,
} from '@common/types';
import { CLIENT_ENV } from '@constants/env';
import { SUPPORTED_LANGUAGES, TSupportedLanguage } from '@constants/languages';
import { clsx, type ClassValue } from 'clsx';
import { TFunction } from 'i18next';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getDefaultLanguage(): TSupportedLanguage {
  return SUPPORTED_LANGUAGES.find((lng) => lng.isDefault)!;
}

function getInstanceFromUrl() {
  const pathParts = window.location.pathname.split('/');
  const instanceIndex = pathParts.indexOf('instance');
  return instanceIndex !== -1 ? pathParts[instanceIndex + 1] : null;
}
function getCurrentInstance(): IInstanceConfig {
  if (CLIENT_ENV) {
    const instance = getInstanceFromUrl();
    switch (CLIENT_ENV) {
      case Client.Incyte:
        if (instance) {
          const result = incyteConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return incyteConfig.instances[0];
      case Client.Hexal:
        if (instance) {
          const result = hexalConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return hexalConfig.instances[0];
      case Client.Novartis:
        if (instance) {
          const result = novartisConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return novartisConfig.instances[0];
      case Client.GSK:
        if (instance) {
          const result = gskConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return gskConfig.instances[0];
      case Client.Seqirus:
        if (instance) {
          const result = seqirusConfig.instances.find((item) => item.name === instance);
          if (result) return result;
          throw new Error('[lib.utils]: No instance found');
        }
        return seqirusConfig.instances[0];
      default:
        throw new Error('[lib.utils]: No instance found');
    }
  } else {
    throw new Error('[lib.utils]: No client detected');
  }
}
export const CURRENT_INSTANCE = getCurrentInstance();

export function getLangNamespace() {
  return CURRENT_INSTANCE.i18nNamespace;
}
export function getThemeName() {
  return CURRENT_INSTANCE.themeName;
}

export function getExactTopicFromTopicsList(
  topicsList: ITopic[],
  search: string
): TNullable<ITopic> {
  for (const topic of topicsList) {
    if (
      getTopicMatchingTerms(topic).some((term) => {
        const normalizedTerm = normalizeQueryForTopicMatching(term);
        return normalizedTerm.length > 0 && normalizedTerm === search;
      })
    ) {
      return topic;
    }
  }
  return null;
}
export function getExactGroupFromTopicGroupsList(
  topicGroups: ITopicGroup[],
  search: string
): TNullable<ITopicGroup> {
  const foundTopicGroupByName = topicGroups.find(
    (topicGroup) => normalizeQueryForTopicMatching(topicGroup.name) === search
  );
  if (foundTopicGroupByName) return foundTopicGroupByName;

  for (const topicGroup of topicGroups) {
    if (
      topicGroup.synonyms?.some((synonym) => normalizeQueryForTopicMatching(synonym) === search)
    ) {
      return topicGroup;
    }
  }
  return null;
}
export function getExactTagFromTopicTagsList(
  topicTags: ITopicTag[],
  search: string
): TNullable<ITopicTag> {
  return (
    topicTags.find((topicTag) => normalizeQueryForTopicMatching(topicTag.name) === search) || null
  );
}
export function getOnlyDisabledTopicFromGroup(
  topics: ITopic[],
  topicGroup: TNullable<ITopicGroup>
): TNullable<ITopic> {
  if (!topicGroup) {
    return null;
  }

  const topicsInGroup = topics.filter((topic) => topic.groupId === topicGroup.id);
  if (topicsInGroup.length === 0) {
    return null;
  }

  return topicsInGroup.every((topic) => topic.status === 'disabled') ? topicsInGroup[0] : null;
}
export function getOnlyDisabledTopicFromTag(
  topics: ITopic[],
  topicTag: TNullable<ITopicTag>
): TNullable<ITopic> {
  if (!topicTag) {
    return null;
  }

  const topicsWithTag = topics.filter((topic) => topic.tags?.some((tag) => tag.id === topicTag.id));
  if (topicsWithTag.length === 0) {
    return null;
  }

  return topicsWithTag.every((topic) => topic.status === 'disabled') ? topicsWithTag[0] : null;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeQueryForTermBoundaryMatching(query: string) {
  return trimQuery(query)
    .toLocaleLowerCase()
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ');
}

function includesSearchTerm(query: string, search: string) {
  const normalizedSearch = normalizeQueryForTopicMatching(search);
  if (!normalizedSearch) {
    return false;
  }

  const searchPattern = escapeRegExp(normalizedSearch).replace(/\s+/g, '\\s+');
  const searchTermPattern = new RegExp(
    `(^|[^\\p{L}\\p{N}])${searchPattern}(?=$|[^\\p{L}\\p{N}])`,
    'u'
  );
  return (
    searchTermPattern.test(normalizeQueryForTopicMatching(query)) ||
    searchTermPattern.test(normalizeQueryForTermBoundaryMatching(query))
  );
}

export function findFirstTopicInQuery(query: string, topics: ITopic[]) {
  let matchedTopic: ITopic | undefined;
  let matchedKeywordLength = 0;

  for (const topic of topics) {
    for (const matchingTerm of getTopicMatchingTerms(topic)) {
      const normalizedTerm = normalizeQueryForTopicMatching(matchingTerm);
      if (includesSearchTerm(query, matchingTerm)) {
        if (normalizedTerm.length > matchedKeywordLength) {
          matchedTopic = topic;
          matchedKeywordLength = normalizedTerm.length;
        }
      }
    }
  }

  return matchedTopic;
}
export function findFirstTopicGroupInQuery(query: string, topicGroups: ITopicGroup[]) {
  for (const topicGroup of topicGroups) {
    if (includesSearchTerm(query, topicGroup.name)) {
      return topicGroup;
    }
  }
  for (const topicGroup of topicGroups) {
    if (topicGroup.synonyms?.length > 0) {
      for (const synonym of topicGroup.synonyms) {
        if (includesSearchTerm(query, synonym)) {
          return topicGroup;
        }
      }
    }
  }
}
export function findFirstTopicTagInQuery(query: string, topicTags: ITopicTag[]) {
  for (const topicTag of topicTags) {
    if (includesSearchTerm(query, topicTag.name)) {
      return topicTag;
    }
  }
}

export type TTopicSelectionMatch =
  | { type: 'tag'; value: ITopicTag }
  | { type: 'group'; value: ITopicGroup }
  | { type: 'topic'; value: ITopic };

export function findBestTopicSelectionMatchInQuery(
  query: string,
  topics: ITopic[],
  topicGroups: ITopicGroup[],
  topicTags: ITopicTag[]
): TTopicSelectionMatch | undefined {
  const firstTopicTag = findFirstTopicTagInQuery(query, topicTags);
  const firstTopicGroup = findFirstTopicGroupInQuery(query, topicGroups);
  const firstTopic = findFirstTopicInQuery(query, topics);
  const candidates: Array<{ match: TTopicSelectionMatch; terms: string[] }> = [];

  if (firstTopicTag) {
    candidates.push({ match: { type: 'tag', value: firstTopicTag }, terms: [firstTopicTag.name] });
  }
  if (firstTopicGroup) {
    candidates.push({
      match: { type: 'group', value: firstTopicGroup },
      terms: [firstTopicGroup.name, ...(firstTopicGroup.synonyms || [])],
    });
  }
  if (firstTopic) {
    candidates.push({
      match: { type: 'topic', value: firstTopic },
      terms: getTopicMatchingTerms(firstTopic),
    });
  }

  let bestMatch: TTopicSelectionMatch | undefined;
  let bestMatchLength = 0;

  for (const candidate of candidates) {
    const matchLength = candidate.terms.reduce((longestLength, term) => {
      if (!includesSearchTerm(query, term)) {
        return longestLength;
      }
      return Math.max(longestLength, normalizeQueryForTopicMatching(term).length);
    }, 0);

    if (matchLength > bestMatchLength) {
      bestMatch = candidate.match;
      bestMatchLength = matchLength;
    }
  }

  return bestMatch;
}

export function trimQuery(query: string) {
  return query.trim();
}

function getTopicMatchingTerms(topic: ITopic) {
  return [topic.displayName, ...(topic.keywords || [])];
}

export function normalizeQueryForTopicMatching(query: string) {
  return trimQuery(query)
    .toLocaleLowerCase()
    .replace(/\p{Pd}+/gu, ' ')
    .replace(/[\p{P}\p{S}]+/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function convertQueryToMessage(query: string, total: number): IMessage {
  const id = ++total;
  return {
    id,
    message: query,
    type: MessageType.UserEntry,
  };
}
export function convertResponseToMessage(
  response: INlpResponse,
  total: number,
  t: TFunction
): IMessage {
  if (!response?.questionOptions) {
    const message = t('common.general_error');
    return {
      id: total,
      message: message || 'error',
      type: MessageType.Error,
    };
  }

  const firstOption = response.questionOptions[0];

  if (!firstOption) {
    return {
      id: total,
      message: t('chat.response.no_answer'),
      type: MessageType.NoAnswerResponse,
    };
  }

  const option = firstOption.optionType;
  if (option === 'CERTAIN_OPTION') {
    return questionOptionToMessage(
      firstOption,
      response.questionOptions,
      response.query!.id,
      t,
      total
    );
  }
  return {
    id: total,
    message: t('chat.response.not_sure'),
    choices: response.questionOptions,
    type: MessageType.ChoiceResponse,
  };
}
export function questionOptionToMessage(
  question: IQuestionOption,
  questionOptions: IQuestionOption[],
  queryId: string,
  t: TFunction,
  total: number
): IMessage {
  const type = question.status === 'infoservice' ? MessageType.ForceMedInfo : MessageType.Response;
  return {
    id: total,
    questionQuery: question.actualQuery,
    questionId: question.questionId,
    message: question.answer || t('chat.response.no_text'),
    queryId,
    type,
    references: question.references || [],
    choices: questionOptions,
  };
}
export function shouldUseSessionStorage() {
  const instancesUsingSessionStorage: string[] = [HexalInstance.Default];
  return instancesUsingSessionStorage.includes(CURRENT_INSTANCE.name);
}
export function shouldUseMaxHight() {
  const instancesUsingMaxHight: string[] = [HexalInstance.Default];
  return instancesUsingMaxHight.includes(CURRENT_INSTANCE.name);
}
export function shouldDisableSearchFieldAutoFocus() {
  const instancesWithDisabledInput: string[] = [HexalInstance.Default];
  return instancesWithDisabledInput.includes(CURRENT_INSTANCE.name);
}

export function isNovartisInstance() {
  return CURRENT_INSTANCE.name === NovartisInstance.Default;
}

export function buildNovartisContactFormUrl(query: string) {
  const trimmedQuery = trimQuery(query);
  const searchParams = new URLSearchParams({
    message: trimmedQuery,
  });

  return `https://klinischeforschung.novartis.de/infoservice/?${searchParams.toString()}#modal-kontaktformular`;
}

export function getNocacheDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}${month}${day}`;
}

export function addNocacheToUrl(url: string, date = new Date()): string {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return url;
  }

  const hashIndex = trimmedUrl.indexOf('#');
  const urlWithoutHash = hashIndex === -1 ? trimmedUrl : trimmedUrl.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : trimmedUrl.slice(hashIndex);
  const queryIndex = urlWithoutHash.indexOf('?');
  const baseUrl = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
  const searchParams = new URLSearchParams(query);

  searchParams.set('nocache', getNocacheDate(date));

  return `${baseUrl}?${searchParams.toString()}${hash}`;
}

export function getExternalDocumentLink(reference: IReference): string {
  if (!reference.externalDocumentLink) {
    return '';
  }

  if (reference.referenceType !== ReferenceType.Smpc) {
    return reference.externalDocumentLink;
  }

  return addNocacheToUrl(reference.externalDocumentLink);
}

export const QUERY_PARAM = 'query';
export const SUBMIT_QUERY_PARAM = 'submitQuery';
